import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AddLayerIcon } from '@components/icons/icons/AddLayerIcon';
import { IconButton } from '@components/buttons/IconButton/IconButton';
import Modal from '@components/Modal/Modal';
import { useDropzone } from 'react-dropzone';
import { Form, Row } from '@components/form/Form/Form';
import * as FileField from '@components/form/FileField/FileField';
import * as TextField from '@components/form/TextField/TextField';
import { projectsApi } from '@api/projectsApi';
import { useTypedSelector } from '@redux/Hooks/storeSelectors';
import {
    selectCurrentProjectBorders,
    selectCurrentProjectCenter,
    selectCurrentProjectId,
    selectSourcesGroups,
    selectViewPortCoordinates,
} from '@redux/selectors/selectors';
import { useTheme } from '@context/themeContext';
import { themeColors } from '@styles/theme';
import { isErrorResponse, safelyParseJson } from '@utils/utils';
import { entityMapLayersToSourcesAndLayers } from '@utils/mapUtils';
import { addedLayers, addedSources } from '@redux/map/map-reducer';
import { useDispatch } from 'react-redux';
import Tooltip from '@mui/material/Tooltip';
import { useToastContext } from '@context/toastContext';
import { MapboxLayerType } from '@api/enums/enums';
import { useImageLayer } from '@context/imageLayerContext';
import { ViewPortCoordinates } from '@api/entities/local/Borders';
import * as SelectBox from '@components/form/SelectBox/SelectBox';
import { Option } from '@components/form/SelectBox/SelectBox';
import { InfoIcon } from '@icons/icons/InfoIcon';
import { MAP_COMMON_SOURCES } from '@utils/constants';

const ACCEPTED_FILES = {
    'application/json': ['.json'],
};

const NewLayerFileUploader = () => {
    const [showUploader, setShowUploader] = useState(false);
    const [disabledSubmit, setDisabledSubmit] = useState(true);
    const [fileType, setFileType] = useState<'file' | 'image'>('file');
    const projectId = useTypedSelector(selectCurrentProjectId);
    const centerCoords = useTypedSelector(selectCurrentProjectCenter);
    const projBordersFeature = useTypedSelector(selectCurrentProjectBorders);
    const viewPortCoordinates = useTypedSelector<ViewPortCoordinates>(selectViewPortCoordinates);
    const sourcesGroups = useTypedSelector(selectSourcesGroups);
    const { t } = useTranslation();
    const { theme } = useTheme();
    const { addToast } = useToastContext();
    const dispatch = useDispatch();

    const editor = useImageLayer();

    const { acceptedFiles, fileRejections, getRootProps, getInputProps, open, isDragActive } =
        useDropzone({
            // noClick: true,
            noKeyboard: true,
            accept: ACCEPTED_FILES,
            maxFiles: 1,
        });

    const layerTypeOptions: Option[] = useMemo(
        () => [
            {
                value: 'file',
                caption: 'From file',
            },
            {
                value: 'image',
                caption: 'From image',
            },
        ],
        []
    );

    const layersGroupsOptions: Option[] = useMemo(() => {
        return sourcesGroups.map((group, idx) => ({
            value: group,
            caption: group,
        }));
    }, [sourcesGroups]);

    const handleLayerTypeChange = useCallback(
        event => {
            const { value } = event.target;
            setFileType(value);
        },
        [setFileType]
    );

    const handleClick = useCallback(() => {
        setShowUploader(true);
    }, [setShowUploader]);

    const formChangeHandler = useCallback(event => {
        const formData = new FormData(event.currentTarget);
        setDisabledSubmit(() => {
            const file = formData.get('geoJsonFileInput');
            return !formData.get('layerName') || (file as File)?.size <= 0;
        });
    }, []);

    const onFormSubmit = useCallback(
        async event => {
            const formData = new FormData(event.target);

            if (fileType === 'file') {
                const entityMapLayers = await projectsApi.uploadGeojsonLayer(projectId, formData);

                if (isErrorResponse(entityMapLayers)) {
                    const parsedErr =
                        typeof entityMapLayers.error === 'string'
                            ? safelyParseJson(entityMapLayers.error)?.error
                            : entityMapLayers.error.error;
                    addToast(parsedErr, 'error');
                } else {
                    const { sources, layers } = entityMapLayersToSourcesAndLayers(entityMapLayers);
                    dispatch(addedSources(sources));
                    dispatch(addedLayers(layers));
                    addToast(t('layers-list.add_new_layer_success_msg'), 'success');
                }
            } else if (fileType === 'image') {
                const url = URL.createObjectURL(formData.get('image') as File);
                const { minLon, maxLon, minLat, maxLat } = viewPortCoordinates;

                const coordinates = [
                    [minLon, maxLat],
                    [maxLon, maxLat],
                    [maxLon, minLat],
                    [minLon, minLat],
                ];

                const source = {
                    id: MAP_COMMON_SOURCES.imageLayer,
                    type: MapboxLayerType.image,
                    displayName: formData.get('layerName') as string,
                    viewName: formData.get('layerName') as string,
                    url: url,
                    coordinates: coordinates,
                    entityName: '',
                    _entityName: '',
                    needAlgorithmId: false,
                    showByDefault: true,
                    order: 0,
                    opacity: 0.1,
                    dayTimeDependent: false,
                    queryable: false,
                    clusterize: false,
                    clickable: true,
                    updateOnFeatureSelection: false,
                    group: formData.get('layersGroup') as string,
                    hasBalloon: false,
                };

                const { sources, layers } = entityMapLayersToSourcesAndLayers([source]);
                dispatch(addedSources(sources));
                dispatch(addedLayers(layers));

                const resizeSource = {
                    id: MAP_COMMON_SOURCES.resizeLayer,
                    type: MapboxLayerType.STOPS,
                    displayName: 'Image resizer',
                    viewName: 'Image resizer',
                    entityName: 'SomeEntity',
                    _entityName: 'SomeEntity',
                    needAlgorithmId: false,
                    showByDefault: true,
                    order: 100,
                    opacity: 1,
                    dayTimeDependent: false,
                    queryable: false,
                    clusterize: false,
                    clickable: true,
                    updateOnFeatureSelection: false,
                    group: formData.get('layersGroup') as string,
                    hasBalloon: false,
                };

                const { sources: resizeSources, layers: resizeLayers } =
                    entityMapLayersToSourcesAndLayers([resizeSource]);

                editor.setCoords(coordinates as any);

                dispatch(addedSources(resizeSources));
                dispatch(addedLayers(resizeLayers));

                addToast(t('layers-list.add_new_layer_success_msg'), 'success');
            }

            setShowUploader(false);
        },
        [projectId, centerCoords, projBordersFeature, viewPortCoordinates, fileType]
    );

    return (
        <>
            <Tooltip title={t('layers-list.add_new_layer_btn')} placement="top">
                <IconButton
                    icon={AddLayerIcon}
                    width={36}
                    height={36}
                    onClick={handleClick}
                    iconColor={theme === 'light' ? themeColors.navyBlue : themeColors.white}
                    backgroundColor={theme === 'light' ? themeColors.grey6 : themeColors.grey5}
                />
            </Tooltip>
            {showUploader ? (
                <Modal
                    title={t('layers-list.add_new_layer_form_title')}
                    setVisible={setShowUploader}
                    testId="layerUploadForm"
                >
                    <Form
                        name="layerUploadForm"
                        onSubmit={onFormSubmit}
                        submitBtnTitle={t('layers-list.add_new_layer_form_submit_btn')}
                        disabled={disabledSubmit}
                        onChange={formChangeHandler}
                    >
                        <Row>
                            <TextField.Labeled
                                label={t('layers-list.add_new_layer_form_field_name')}
                                name="layerName"
                                type="text"
                                testId="projectSimulationFormNameInput"
                                required
                                autoFocus
                            />
                        </Row>
                        <Row>
                            <SelectBox.Labeled
                                label={t('layers-list.add_new_layer_form_field_layer-source')}
                                name="fileType"
                                onChange={handleLayerTypeChange}
                                options={layerTypeOptions}
                            />
                        </Row>
                        {fileType === 'file' ? (
                            <Row>
                                <FileField.Labeled
                                    label={t('layers-list.add_new_layer_form_field_file')}
                                    name="geoJsonFileInput"
                                    required={true}
                                    acceptedFileExt={{ 'application/json': ['.geojson'] }}
                                />
                            </Row>
                        ) : (
                            <>
                                <Row>
                                    <SelectBox.Labeled
                                        label={t(
                                            'layers-list.add_new_layer_form_field_layer-group'
                                        )}
                                        name="layersGroup"
                                        // onChange={handleLayersGroup}
                                        value={layersGroupsOptions?.[0]?.value}
                                        options={layersGroupsOptions}
                                    />
                                    <Tooltip
                                        title={t(
                                            'layers-list.add_new_layer_form_field_layer-group-tooltip'
                                        )}
                                    >
                                        <IconButton
                                            icon={InfoIcon}
                                            width={20}
                                            height={20}
                                            borderless
                                        />
                                    </Tooltip>
                                </Row>
                                <Row>
                                    <FileField.Labeled
                                        label={t('layers-list.add_new_layer_form_field_image')}
                                        name="image"
                                        required={true}
                                        acceptedFileExt={{
                                            'image/png': ['.png'],
                                            'image/jpeg': ['.jpg', '.jpeg'],
                                        }}
                                    />
                                </Row>
                            </>
                        )}
                    </Form>
                </Modal>
            ) : null}
        </>
    );
};

export default NewLayerFileUploader;
